import { Edit, ListButton, RefreshButton, useForm, useSelect } from "@refinedev/antd";
import { DatePicker, Form, Input, Select, Space, Tag } from "antd";
import { IResourceComponentsProps } from "@refinedev/core";
import { CloudServerOutlined, DollarOutlined, SettingOutlined, KeyOutlined, LinkOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ICurrency, IMetadataAvcs, ISubscription } from "interfaces";
import React from "react";
import { FormSection, FormFieldGrid } from "components/form";
import { PricingFields } from "components/metadata/PricingFields";
import { enrollmentDateFieldProps } from "components/metadata/enrollmentDate";

export const MetadataAvcsEdit: React.FC<IResourceComponentsProps> = () => {
  const {
    formProps,
    saveButtonProps,
    query,
    formLoading: isLoading,
  } = useForm<IMetadataAvcs>({
    action: "edit",
  });

  const { selectProps: currencySelectProps } = useSelect<ICurrency>({
    resource: "currency",
    optionLabel: "code",
    optionValue: "code",

    pagination: {
      mode: "server"
    }
  });

  function SubscriptionSelectComponent() {
    const { selectProps } = useSelect<ISubscription>({
      resource: "subscription",
      optionLabel: "subscriptionName",
      optionValue: "id",

      filters: query?.data?.data?.subscription?.id
        ? [
            {
              field: "id",
              operator: "eq",
              value: query?.data?.data?.subscription?.id,
            },
          ]
        : [],

      pagination: {
        mode: "server"
      }
    });
    return (
      <Form.Item
        label="Subscription"
        name={["subscription", "id"]}
        rules={[{ required: true }]}
        initialValue={query?.data?.data?.subscription?.subscriptionName}
      >
        <Select {...selectProps} disabled />
      </Form.Item>
    );
  }

  return query?.isLoading ? (
    <React.Fragment>'Loading...'</React.Fragment>
  ) : (
    <Edit
      breadcrumb={false}
      saveButtonProps={saveButtonProps}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Edit AVCS Service",
        subTitle: "edit AVCS service",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        ghost: true,
        extra: (
          <React.Fragment>
            <Space>
              <ListButton>AVCS Service</ListButton>
              <RefreshButton
                resource="metadata-avcs"
                recordItemId={query?.data?.data?.id}
              />
            </Space>
          </React.Fragment>
        ),
      }}
    >
      <Form {...formProps} layout="vertical">
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <FormSection
            title="Service Configuration"
            subtitle="Product and subscription settings"
            icon={<CloudServerOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              {query?.isSuccess ? (
                <SubscriptionSelectComponent />
              ) : null}
              <Form.Item
                label="Product Name"
                name="productName"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "AVCSCharts", value: "AVCSCharts" },
                    { label: "ARCSCharts", value: "ARCSCharts" },
                    { label: "ADP", value: "ADP" },
                    { label: "ENP", value: "ENP" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Product Group"
                name="productGroup"
                rules={[{ required: false }]}
              >
                <Select
                  options={[
                    { label: "AVCS Products", value: "AVCS Products" },
                    { label: "ARCS Products", value: "ARCS Products" },
                    { label: "ADP", value: "ADP" },
                    { label: "e-NPs", value: "e-NPs" },
                  ]}
                />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>

          <FormSection
            title="Billing Information"
            subtitle="Pricing and payment configuration"
            icon={<DollarOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true }]}
              >
                <Select {...currencySelectProps} />
              </Form.Item>
              <Form.Item
                label="Subscription Type"
                name="subscriptionType"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "Pay Per Use - Quotation", value: "Pay Per Use - Quotation" },
                    { label: "Flat Fee Service - Worldwide", value: "Flat Fee Service - Worldwide" },
                    { label: "Flat Fee Service - Fixed Run", value: "Flat Fee Service - Fixed Run" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Payment Frequency"
                name="paymentFrequency"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "Annual", value: "Annual" },
                    { label: "Semi-Annual", value: "Semi-Annual" },
                    { label: "Quarterly", value: "Quarterly" },
                    { label: "Monthly", value: "Monthly" },
                  ]}
                />
              </Form.Item>
              <PricingFields form={formProps.form} />
            </FormFieldGrid>
          </FormSection>

          <FormSection
            title="Licence Details"
            subtitle="Activation and expiry settings"
            icon={<SettingOutlined />}
            variant="elevated"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Licence Type"
                name="licenceType"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Activation Status"
                name="activationStatus"
                rules={[{ required: false }]}
              >
                <Select
                  options={[
                    { label: "active", value: "active" },
                    { label: "inactive", value: "inactive" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Enrollment Date"
                name="enrollmentDate"
                rules={[{ required: true }]}
                {...enrollmentDateFieldProps}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                label="Expiry Date"
                name="expiryDate"
                rules={[{ required: true }]}
                getValueProps={(value) => ({
                  value: value ? dayjs(value) : "",
                })}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>

          <FormSection
            title="Permit Configuration"
            subtitle="Master, backup and reserve permits"
            icon={<KeyOutlined />}
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Master Permit"
                name="masterPermit"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Master Permit Version"
                name="masterPermitVersion"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Backup Permit"
                name="backupPermit"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Backup Permit Version"
                name="backupPermitVersion"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Reserve1 Permit"
                name="reserve1Permit"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Reserve1 Permit Version"
                name="reserve1PermitVersion"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Reserve2 Permit"
                name="reserve2Permit"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Reserve2 Permit Version"
                name="reserve2PermitVersion"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Reserve3 Permit"
                name="reserve3Permit"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Reserve3 Permit Version"
                name="reserve3PermitVersion"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>

          <FormSection
            title="Certificate URLs"
            subtitle="Document locations"
            icon={<LinkOutlined />}
            variant="subtle"
          >
            <FormFieldGrid columns={2}>
              <Form.Item
                label="Certificate URL"
                name="certificateUrl"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Schedule-A URL"
                name="scheduleAUrl"
                rules={[{ required: false }]}
              >
                <Input />
              </Form.Item>
            </FormFieldGrid>
          </FormSection>
        </Space>
      </Form>
    </Edit>
  );
};
